import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { fetchPaymentStatus } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tx: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetchPaymentStatus(sessionId);
        if (cancelled) return;
        if (r.payment_status === "paid") { setState({ status: "paid", tx: r }); return; }
        if (r.payment_status === "failed" || r.payment_status === "expired") {
          setState({ status: "failed", tx: r }); return;
        }
        if (attempts >= 10) { setState({ status: "timeout", tx: r }); return; }
        setAttempts((a) => a + 1);
        setTimeout(poll, 2000);
      } catch { if (!cancelled) setState({ status: "error" }); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts]);

  return (
    <div data-testid="payment-success-page" className="container-pad py-24">
      <div className="card-soft p-12 max-w-xl mx-auto text-center">
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-teal/10 flex items-center justify-center"><CheckCircle2 size={40} className="text-teal" /></div>
            <h1 className="font-display text-4xl text-navy mt-6 tracking-tight">Payment received</h1>
            <p className="text-muted2 mt-4">Thanks, we've marked your instalment as paid. If this completed your engagement, your documentation is now unlocked in the portal.</p>
            <Link to="/portal" className="btn-ochre mt-8">Open portal <ArrowRight size={16} /></Link>
          </>
        )}
        {(state.status === "polling") && (
          <>
            <Loader2 size={40} className="text-teal animate-spin mx-auto" />
            <h1 className="font-display text-3xl text-navy mt-6 tracking-tight">Confirming your payment</h1>
            <p className="text-muted2 mt-4">This usually takes a couple of seconds.</p>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Loader2 size={40} className="text-teal mx-auto" />
            <h1 className="font-display text-3xl text-navy mt-6 tracking-tight">Payment processing</h1>
            <p className="text-muted2 mt-4">Your payment is taking a little longer than expected. Refresh the portal in a minute or contact us if it doesn't update.</p>
            <Link to="/portal" className="btn-ghost-navy mt-8">Open portal <ArrowRight size={16} /></Link>
          </>
        )}
        {(state.status === "failed" || state.status === "error") && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center"><XCircle size={40} className="text-red-600" /></div>
            <h1 className="font-display text-3xl text-navy mt-6 tracking-tight">Payment didn't complete</h1>
            <p className="text-muted2 mt-4">Please try again from the portal, or contact us if the issue continues.</p>
            <Link to="/portal" className="btn-ghost-navy mt-8">Back to portal</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="container-pad py-24">
      <div className="card-soft p-12 max-w-xl mx-auto text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-sand flex items-center justify-center"><XCircle size={40} className="text-navy" /></div>
        <h1 className="font-display text-3xl text-navy mt-6 tracking-tight">Payment cancelled</h1>
        <p className="text-muted2 mt-4">No charge was made. You can retry from your portal at any time.</p>
        <Link to="/portal" className="btn-ochre mt-8">Back to portal <ArrowRight size={16} /></Link>
      </div>
    </div>
  );
}
